import { captureMessage } from '@sentry/nextjs';
import { Plan } from 'api/savings-plans';
import { SavingsPlan } from 'interfaces/customer';
import { axios } from 'utils/axios';
import {
  SmartCollectionInput,
  SmartCollectionResponse,
  isSmartCollectionInputWithSlug,
} from './types';
import { cleanUpInput, isSmartCollectionResponse } from './utils';

/**
 * Fetch collection using Smart Collection API.
 * Returns personalized product recommendations based on input parameters and audience targeting.
 */
export const getSmartCollection = async (
  input: SmartCollectionInput,
  activePlan?: Plan | SavingsPlan | null
): Promise<SmartCollectionResponse> => {
  let audiences: undefined | string | number | boolean;
  const payload = {
    ...cleanUpInput(input),
    ...(activePlan?.ruleId && {
      rule_id: activePlan.ruleId,
    }),
  };

  const result = await axios
    .post<SmartCollectionResponse>('/api/legacy/smart-collections', payload, { isPdexApi: true })
    .then((data) => {
      audiences = data.config.headers && data.config.headers['audiences'];
      return data;
    })
    .then(({ data }) => data)
    .catch((error) => {
      throw new Error(
        `Smart Collection API request failed: ${error.message}. Input: ${JSON.stringify(payload)}`
      );
    });

  if (!result) {
    throw new Error(
      `Smart Collection API returned empty response for input: ${JSON.stringify(payload)}`
    );
  }

  if (isSmartCollectionResponse(result) && result.items.length === 0) {
    const slug = isSmartCollectionInputWithSlug(input) ? input.slug : 'no-slug';
    captureMessage(`Smart Collection API returned empty items array for slug: ${slug}`, {
      level: 'debug',
      extra: {
        input: cleanUpInput(input),
        audiences: audiences,
        endpoint: '/api/legacy/smart-collections',
      },
    });
  }

  return result;
};
